/**
 * Query Cache Persistence for Offline-First Support
 * 
 * Uses IndexedDB (via idb-keyval) to persist React Query cache
 * across sessions, enabling instant load and offline functionality.
 */

import { get, set, del } from 'idb-keyval';

const CACHE_KEY = 'agxl-query-cache';
const CACHE_VERSION = 1;
const MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 hours

/** Persisted client state structure */
export interface PersistedClientState {
  queries: Array<{
    queryKey: readonly unknown[];
    queryHash: string;
    state: unknown;
  }>;
  mutations: unknown[];
}

export interface CacheMetadata {
  version: number;
  timestamp: number;
  clientState: PersistedClientState;
}

/**
 * Save the React Query client state to IndexedDB
 */
export async function persistQueryCache(client: PersistedClientState): Promise<void> {
  try {
    const metadata: CacheMetadata = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      clientState: client,
    };
    await set(CACHE_KEY, metadata);
    console.log('📦 Query cache persisted');
  } catch (error) {
    console.warn('Failed to persist query cache:', error);
  }
}

/**
 * Restore the React Query client state from IndexedDB
 * Returns undefined if cache is invalid or expired
 */
export async function restoreQueryCache(): Promise<PersistedClientState | undefined> {
  try {
    const metadata = await get<CacheMetadata>(CACHE_KEY);
    
    if (!metadata) {
      console.log('📦 No cached query state found');
      return undefined;
    }

    // Check version compatibility
    if (metadata.version !== CACHE_VERSION) {
      console.log('📦 Cache version mismatch, clearing');
      await clearQueryCache();
      return undefined;
    }

    // Check if cache is expired
    const age = Date.now() - metadata.timestamp;
    if (age > MAX_AGE_MS) {
      console.log('📦 Cache expired, clearing');
      await clearQueryCache();
      return undefined;
    }

    console.log(`📦 Restored query cache (age: ${Math.round(age / 1000 / 60)}min)`);
    return metadata.clientState;
  } catch (error) {
    console.warn('Failed to restore query cache:', error);
    return undefined;
  }
}

/**
 * Clear the persisted query cache
 */
export async function clearQueryCache(): Promise<void> {
  try {
    await del(CACHE_KEY);
    console.log('📦 Query cache cleared');
  } catch (error) {
    console.warn('Failed to clear query cache:', error);
  }
}

/**
 * Create an IDB-based persister for React Query
 * Compatible with @tanstack/query-sync-storage-persister
 */
export function createIDBPersister() {
  return {
    persistClient: persistQueryCache,
    restoreClient: restoreQueryCache,
    removeClient: clearQueryCache,
  };
}

/**
 * Check if IndexedDB is available
 */
export function isIDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}
